import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, X, UserCircle, Shield } from 'lucide-react';
import Swal from 'sweetalert2';
import URLS from '../config/urls';
import clientDetails from '../constants/clientDetails';
import { useExternalScript } from '../util/useExternalScript';
import { SIGN_IN_BUTTON_PLUGIN_URL } from '../constants/cred';
import axiosInstance from '../config/axiosConfig';

const LoginPage = () => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);
  const [showAdminPopup, setShowAdminPopup] = useState(true);
  const [adminCredentials, setAdminCredentials] = useState({
    username: '',
    password: ''
  });
  const [adminError, setAdminError] = useState('');
  const [adminLoading, setAdminLoading] = useState(false);

  const navigate = useNavigate();
  const signInButtonScript = SIGN_IN_BUTTON_PLUGIN_URL;

  const state = useExternalScript(signInButtonScript);

  useEffect(() => {
    checkAdminSession();
  }, []);

  const checkAdminSession = () => {
    const adminToken = sessionStorage.getItem('adminToken');
    const tokenExpiry = sessionStorage.getItem('adminTokenExpiry');

    if (adminToken && tokenExpiry) {
      const now = new Date().getTime();
      if (now < parseInt(tokenExpiry)) {
        setIsAdminAuthenticated(true);
      } else {
        sessionStorage.removeItem('adminToken');
        sessionStorage.removeItem('adminTokenExpiry');
      }
    }
  };

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setAdminError('');

    // Input validation
    const { username, password } = adminCredentials;

    // Validate username (email format)
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!username || !username.trim()) {
      setAdminError('Username is required');
      return;
    }
    if (!emailRegex.test(username)) {
      setAdminError('Please enter a valid email address');
      return;
    }

    // Validate password
    if (!password || !password.trim()) {
      setAdminError('Password is required');
      return;
    }
    if (password.length < 6) {
      setAdminError('Password must be at least 6 characters long');
      return;
    }

    setAdminLoading(true);

    try {
      const response = await axiosInstance.post(URLS.ADMIN_LOGIN, {
        username: username.trim(),
        password: password
      });

      // Validate response structure
      if (!response.data) {
        throw new Error('Invalid response from server');
      }

      // CRITICAL: Check if login was successful
      if (response.data.success !== true) {
        throw new Error(response.data.message || 'Authentication failed');
      }

      const { token, expiresIn } = response.data;

      // Validate token
      if (!token || typeof token !== 'string' || token.trim() === '') {
        throw new Error('Invalid authentication token received');
      }

      // Validate expiry
      if (!expiresIn) {
        throw new Error('Invalid token expiry received');
      }

      // Validate expiry is in the future
      const expiryTime = parseInt(expiresIn);
      const currentTime = new Date().getTime();
      if (isNaN(expiryTime) || expiryTime <= currentTime) {
        throw new Error('Invalid or expired token received');
      }

      // Clear any existing tokens before setting new ones
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminTokenExpiry');

      // Store validated credentials
      sessionStorage.setItem('adminToken', token);
      sessionStorage.setItem('adminTokenExpiry', expiryTime.toString());

      setIsAdminAuthenticated(true);
      setShowAdminPopup(false);

      Swal.fire({
        icon: 'success',
        title: 'Login Successful!',
        text: 'You can now assist users with the feedback form.',
        confirmButtonColor: '#1565C0',
        confirmButtonText: 'Continue',
        timer: 3000,
        timerProgressBar: true
      });

      setAdminCredentials({ username: '', password: '' });

    } catch (error) {
      // Clear any tokens on error
      sessionStorage.removeItem('adminToken');
      sessionStorage.removeItem('adminTokenExpiry');

      if (error.response) {
        // Server responded with error
        if (error.response.status === 401 || error.response.status === 400) {
          setAdminError('Invalid username or password');
        } else if (error.response.status === 403) {
          setAdminError('Access denied. You do not have admin privileges.');
        } else if (error.response.status >= 500) {
          setAdminError('Server error. Please try again later.');
        } else {
          setAdminError(error.response?.data?.message || 'Login failed. Please try again.');
        }
      } else if (error.request) {
        // Request made but no response received
        setAdminError('Unable to connect to server. Please check your internet connection.');
      } else {
        // Validation or other errors
        setAdminError(error.message || 'Login failed. Please try again.');
      }
    } finally {
      setAdminLoading(false);
    }
  };

  const handleOpenAdminPopup = () => {
    setShowAdminPopup(true);
    setAdminError('');
    setAdminCredentials({ username: '', password: '' });
  };

  const handleCloseAdminPopup = () => {
    setShowAdminPopup(false);
    setAdminError('');
    setAdminCredentials({ username: '', password: '' });
  };

  const renderSignInButton = () => {
    const oidcConfig = {
      authorizeUri: clientDetails.uibaseUrl + clientDetails.authorizeEndpoint,
      redirect_uri: clientDetails.redirect_uri_userprofile,
      client_id: clientDetails.clientId,
      scope: clientDetails.scopeUserProfile,
      nonce: clientDetails.nonce,
      state: clientDetails.state,
      acr_values: clientDetails.acr_values,
      claims_locales: clientDetails.claims_locales,
      display: clientDetails.display,
      prompt: clientDetails.prompt,
      max_age: clientDetails.max_age,
      ui_locales: 'en',
      claims: JSON.parse(decodeURIComponent(clientDetails.userProfileClaims)),
    };

    window.SignInWithEsignetButton?.init({
      oidcConfig: oidcConfig,
      buttonConfig: {
        shape: "soft_edges",
        labelText: "Login with SLUDI",
        width: "100%"
      },
      signInElement: document.getElementById("sign-in-with-esignet"),
    });
  }

  useEffect(() => {
    if (isAdminAuthenticated) {
      renderSignInButton();
    }
  }, [state, isAdminAuthenticated]);

  return (
    <div className="login-page">
      <div className="login-content">
        <div className="login-card">
          {/* Logo Header */}
          <div className="login-header">
            <div className="logo-container">
              <img
                src="/assets/images/sludi-logo.png"
                alt="SLUDI Logo"
                className="sludi-logo"
              />
            </div>
            <div className="header-divider"></div>
          </div>

          {/* Main Content */}
          <div className="login-body">
            {!isAdminAuthenticated ? (
              <div className="welcome-section">
                <div className="icon-badge">
                  <div className="icon-glow"></div>
                  <Lock className="main-icon" />
                </div>

                <h1 className="main-title">Admin Access Required</h1>
                <p className="main-description">
                  Please login as an admin to enable the user feedback form
                </p>

                <button
                  onClick={handleOpenAdminPopup}
                  className="btn-primary"
                >
                  <span className="btn-text">Login as Admin</span>
                  <div className="btn-shine"></div>
                </button>
              </div>
            ) : (
              <div className="welcome-section">
                <div className="icon-badge">
                  <div className="icon-glow"></div>
                  <UserCircle className="main-icon" />
                </div>

                <h1 className="main-title">Welcome</h1>
                <p className="main-description">
                  Sign in with your Digital ID to provide feedback
                </p>

                <div id="sign-in-with-esignet" className="signin-container"></div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="login-footer">
            <div className="footer-divider"></div>
            <p className="footer-text">Powered by SLUDI - Sri Lanka's Digital Identity</p>
          </div>
        </div>
      </div>

      {/* Admin Login Modal */}
      {showAdminPopup && (
        <div
          className="modal-backdrop"
          onClick={handleCloseAdminPopup}
          style={{ backgroundImage: 'url("/assets/images/common-background.png")' }}
        >
          <div
            className="admin-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseAdminPopup}
              className="modal-close"
            >
              <X className="close-icon" />
            </button>

            {/* Modal Header */}
            <div className="modal-header">
              <div className="modal-icon-badge">
                <div className="icon-glow"></div>
                <Shield className="modal-icon" />
              </div>
              <h2 className="modal-title">Admin Login</h2>
              <p className="modal-subtitle">Enter your credentials to continue</p>
            </div>

            {/* Modal Body */}
            <form onSubmit={handleAdminLogin} className="modal-form">
              <div className="input-group">
                <label className="input-label">Username</label>
                <input
                  type="email"
                  value={adminCredentials.username}
                  onChange={(e) => setAdminCredentials(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="admin@sludi.gov.lk"
                  className="input-field"
                  required
                />
              </div>

              <div className="input-group">
                <label className="input-label">Password</label>
                <input
                  type="password"
                  value={adminCredentials.password}
                  onChange={(e) => setAdminCredentials(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter your password"
                  className="input-field"
                  required
                />
              </div>

              {adminError && (
                <div className="error-alert">
                  <div className="error-icon">!</div>
                  <span>{adminError}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={adminLoading}
                className="btn-submit"
              >
                {adminLoading ? (
                  <div className="btn-loading">
                    <div className="loading-spinner"></div>
                    <span>Logging in...</span>
                  </div>
                ) : (
                  <>
                    <span className="btn-text">Login</span>
                    <div className="btn-shine"></div>
                  </>
                )}
              </button>

              <p className="help-note">
                Contact your system administrator if you've forgotten your credentials
              </p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;